import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronUp, 
  Phone, 
  Mail, 
  MapPin, 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin 
} from "lucide-react";
import { 
  fadeInUp, 
  slideInLeft, 
  staggerContainer 
} from '../utils/animations';
import { useAdvancedScrollAnimation } from '../hooks/useAdvancedScrollAnimation';

const Footer = () => {
  const { sectionRef, triggerRef, isMobile } = useAdvancedScrollAnimation({
    fadeOutOnLeave: true,
    mobileBreakpoint: 768,
    fadeThreshold: 0.3
  });

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  return (
    <div ref={triggerRef} className="w-full">
      <motion.footer 
        ref={sectionRef}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className={`
          bg-gray-900 
          text-white 
          py-12 
          md:py-16 
          relative 
          overflow-hidden 
          ${isMobile ? 'px-4' : ''}
        `}
      >
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={containerAnimation}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className={`
              footer-content 
              grid 
              ${isMobile 
                ? 'grid-cols-1 gap-8' 
                : 'grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12'
              }
            `}
          >
            <CompanyInfo isMobile={isMobile} />
            <QuickLinks />
            <ResearchCenters />
            <NewsletterSection />
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mt-12 md:mt-16 pt-8 border-t border-gray-800"
          >
            <InfoCards isMobile={isMobile} />
          </motion.div>

          <CopyrightSection />
        </div>

        <DecorativeElements />
        <ScrollToTop />
      </motion.footer>
    </div>
  );
};

const CompanyInfo = ({ isMobile }) => {
  return (
    <motion.div variants={fadeInUp} className="col-span-1">
      <motion.div 
        className="mb-6"
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <img 
          src="/api/placeholder/120/40" 
          alt="JBF Logo" 
          className="h-10 md:h-12 mx-auto md:mx-0"
        />
      </motion.div>
      <div className="space-y-4 text-center md:text-left">
        <CompanyAddress />
        <ContactInfo />
      </div>
    </motion.div>
  );
};

const CompanyAddress = () => (
  <motion.div 
    variants={fadeInUp}
    className="flex items-start space-x-2"
    whileHover={{ x: 5 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <motion.div
      whileHover={{ rotate: 360 }}
      transition={{ duration: 0.6 }}
    >
      <MapPin className="w-5 h-5 text-amber-400 mt-1" />
    </motion.div>
    <p className="text-gray-400 text-sm md:text-base">
      21 Debitala Lane Ichapur North 24 Pargana<br />
      West Bengal 743144
    </p>
  </motion.div>
);

const ContactInfo = () => (
  <motion.div 
    variants={staggerContainer}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    className="space-y-3 mt-4"
  >
    <ContactItem
      icon={<Phone className="w-5 h-5" />}
      href="tel:+917595033731"
      text="+91 75950 33731"
    />
    <ContactItem
      icon={<Mail className="w-5 h-5" />}
      href="mailto:info@jbf.org"
      text="info@jbf.org"
    />
  </motion.div>
);

const ContactItem = ({ icon, href, text }) => (
  <motion.a 
    variants={fadeInUp}
    href={href}
    className="flex items-center space-x-2 text-gray-400 hover:text-amber-400 transition-colors"
    whileHover={{ x: 5, scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <motion.div
      whileHover={{ rotate: 360 }}
      transition={{ duration: 0.6 }}
    >
      {icon}
    </motion.div>
    <span>{text}</span>
  </motion.a>
);

const QuickLinks = () => {
  const textReveal = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  return (
    <motion.div variants={fadeInUp} className="text-center md:text-left">
      <motion.h4 
        className="text-lg font-semibold mb-6"
        variants={textReveal}
      >
        Useful Links
      </motion.h4>
      <motion.ul 
        variants={staggerContainer}
        className="space-y-3"
      >
        {[
          "JBF@NEWS",
          "Articles",
          "UPCOMING Events",
          "IPR",
          "IMS"
        ].map((link, index) => (
          <FooterLink key={index} href="#" delay={index * 0.1}>
            {link}
          </FooterLink>
        ))}
      </motion.ul>
    </motion.div>
  );
};

const ResearchCenters = () => {
  const textReveal = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  return (
    <motion.div variants={fadeInUp} className="text-center md:text-left">
      <motion.h4 
        className="text-lg font-semibold mb-6"
        variants={textReveal}
      >
        Research Centers
      </motion.h4>
      <motion.ul 
        variants={staggerContainer}
        className="space-y-3"
      >
        {[
          "Centre for Advanced Technologies",
          "Centre for Economic Research",
          "Centre for Historical Studies",
          "Centre for Statistics",
          "Sustainable Development"
        ].map((center, index) => (
          <FooterLink key={index} href="#" delay={index * 0.1}>
            {center}
          </FooterLink>
        ))}
      </motion.ul>
    </motion.div>
  );
};

const FooterLink = ({ href, children, delay = 0 }) => (
  <motion.li 
    variants={fadeInUp}
    custom={delay}
  >
    <motion.a
      href={href}
      className="text-gray-400 hover:text-amber-400 transition-colors inline-block"
      whileHover={{ 
        x: 10, 
        scale: 1.02,
        transition: { type: "spring", stiffness: 300 }
      }}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay }}
    >
      {children}
    </motion.a>
  </motion.li>
);

const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const textReveal = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setEmail("");
  };

  return (
    <motion.div variants={fadeInUp} className="text-center md:text-left">
      <motion.h4 
        className="text-lg font-semibold mb-6"
        variants={textReveal}
      >
        Stay Updated
      </motion.h4>
      <NewsletterForm 
        email={email}
        setEmail={setEmail}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
      <SocialLinks />
    </motion.div>
  );
};

const NewsletterForm = ({ email, setEmail, handleSubmit, isSubmitting }) => (
  <motion.form 
    onSubmit={handleSubmit} 
    className="space-y-4"
    variants={fadeInUp}
  >
    <motion.div 
      className="relative"
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        className="w-full px-4 py-2 rounded bg-gray-800 border border-gray-700 focus:outline-none focus:border-amber-500 transition-colors"
        required
      />
    </motion.div>
    <motion.button
      type="submit"
      className="w-full bg-amber-500 text-white px-6 py-2 rounded hover:bg-amber-600 transition-colors disabled:opacity-50"
      disabled={isSubmitting}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 400 }}
    >
      {isSubmitting ? "Subscribing..." : "Subscribe to Newsletter"}
    </motion.button>
  </motion.form>
);

const SocialLinks = () => (
  <motion.div 
    className="flex space-x-6 mt-8 justify-center md:justify-start"
    variants={staggerContainer}
  >
    {[
      { Icon: Facebook, href: "#" },
      { Icon: Twitter, href: "#" },
      { Icon: Instagram, href: "#" },
      { Icon: Linkedin, href: "#" }
    ].map(({ Icon, href }, index) => (
      <motion.a
        key={index}
        href={href}
        className="text-gray-400 hover:text-amber-400 transition-colors"
        variants={fadeInUp}
        whileHover={{ 
          scale: 1.2, 
          rotate: 5,
          transition: { type: "spring", stiffness: 400 }
        }}
        whileTap={{ scale: 0.9 }}
      >
        <Icon className="w-5 h-5" />
      </motion.a>
    ))}
  </motion.div>
);

const InfoCards = ({ isMobile }) => (
  <div className={`
    grid 
    ${isMobile ? 'grid-cols-1 gap-6' : 'grid-cols-3 gap-6 md:gap-8'} 
    mb-8
  `}>
    <InfoCard
      title="Our Mission"
      content="Fostering positive change through multidisciplinary research and collaboration."
      delay={0.1}
    />
    <InfoCard
      title="Get Involved"
      content="Join our initiatives and contribute to meaningful research and development."
      delay={0.2}
    />
    <InfoCard
      title="Research Focus"
      content="Technology, Economics, Historical studies, and Climate Change research."
      delay={0.3}
    />
  </div>
);

const InfoCard = ({ title, content, delay = 0 }) => (
  <motion.div 
    className="p-6 bg-gray-800 rounded-lg"
    variants={fadeInUp}
    custom={delay}
    whileHover={{ 
      y: -5,
      transition: { type: "spring", stiffness: 300 }
    }}
  >
    <motion.h5 
      className="text-lg font-semibold mb-2"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      {title}
    </motion.h5>
    <motion.p 
      className="text-gray-400"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: delay + 0.2 }}
    >
      {content}
    </motion.p>
  </motion.div>
);

const CopyrightSection = () => (
  <motion.div 
    variants={slideInLeft}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    className="border-t border-gray-800 mt-8 md:mt-12 pt-6 md:pt-8"
  >
    <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
      <motion.p 
        className="text-gray-400 text-center md:text-left text-sm md:text-base"
        variants={fadeInUp}
      >
        &copy; {new Date().getFullYear()} <strong>Jansdell Bharathi Foundation</strong>. 
        All Rights Reserved
      </motion.p>
      <motion.div 
        className="flex space-x-4"
        variants={staggerContainer}
      >
        {["Privacy Policy", "Terms of Service"].map((text, index) => (
          <motion.a 
            key={index}
            href="#"
            className="text-gray-400 hover:text-amber-400 transition-colors text-sm md:text-base"
            whileHover={{ scale: 1.05 }}
            variants={fadeInUp}
          >
            {text}
          </motion.a>
        ))}
      </motion.div>
    </div>
  </motion.div>
);

const DecorativeElements = () => {
  const particleVariants = {
    animate: (i) => ({
      y: [0, -20, 0],
      opacity: [0.3, 0.6, 0.3],
      transition: {
        duration: 3 + i * 0.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    })
  };

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute w-96 h-96 bg-amber-500/5 rounded-full -right-48 -bottom-48"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <motion.div
        className="absolute w-72 h-72 bg-amber-500/5 rounded-full -left-36 top-0"
        animate={{
          scale: [1, 1.15, 1],
          rotate: [0, -60, 0],
          opacity: [0.2, 0.4, 0.2]
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-amber-400/10 rounded-full"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
          variants={particleVariants}
          animate="animate"
          custom={i}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/10" />
    </div>
  );
};

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const threshold = 300;
    let ticking = false;

    const updateVisibility = () => {
      const shouldBeVisible = window.pageYOffset > threshold;
      setIsVisible(shouldBeVisible);
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateVisibility();
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const buttonVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      rotate: -180
    },
    visible: { 
      opacity: 1, 
      y: 0,
      rotate: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: { 
      opacity: 0, 
      y: 20,
      rotate: 180,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    hover: { 
      y: -5,
      scale: 1.1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    },
    tap: { 
      scale: 0.9,
      y: 0
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 bg-amber-500 text-white rounded-full shadow-lg hover:bg-amber-600 z-50"
          initial="hidden"
          animate="visible"
          exit="exit"
          whileHover="hover"
          whileTap="tap"
          variants={buttonVariants}
        >
          <motion.div
            animate={{ y: [0, -3, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <ChevronUp className="w-6 h-6" />
          </motion.div>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default Footer;